import type { FC, ReactNode } from 'react'
import type { LucideIcon } from 'lucide-react'
import type { SocialLink } from '@/types'

import Link from 'next/link'

import {
    Facebook,
    Instagram,
    Mail,
    MapPin,
    MessageCircle,
    Phone
} from 'lucide-react'

import { Container } from '@/components/generals/container'
import { Logo } from '@/components/generals/logo'

import { company, contactHref, navigation, siteConfig } from '@/constants/site'

const socialIcons: Record<SocialLink['network'], LucideIcon> = {
    facebook: Facebook,
    instagram: Instagram,
    whatsapp: MessageCircle,
    google: MapPin
}

export const SiteFooter: FC = (): ReactNode => {
    const socials = company.socials.filter(social => social.href)

    return (
        <footer className='border-t border-border bg-background'>
            <Container className='py-12'>
                <div className='flex flex-col items-center gap-8 lg:flex-row lg:justify-between'>
                    <Link href='/' aria-label={`${siteConfig.name}, naslovna`} className='rounded-md outline-none focus-visible:ring-3 focus-visible:ring-ring/50'>
                        <Logo />
                    </Link>

                    <nav aria-label='Navigacija u podnožju'>
                        <ul className='flex flex-wrap items-center justify-center gap-x-2 gap-y-2 text-xs font-semibold tracking-[0.12em] uppercase'>
                            {navigation.map((item, index) => (
                                <li key={item.href} className='flex items-center gap-2'>
                                    {index > 0 && <span aria-hidden='true' className='text-muted-foreground'>·</span>}

                                    <Link href={item.href} className='rounded-sm px-1 text-foreground/75 transition-colors hover:text-primary'>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}

                            <li className='flex items-center gap-2'>
                                <span aria-hidden='true' className='text-muted-foreground'>·</span>

                                <Link href={contactHref} className='rounded-sm px-1 text-foreground/75 transition-colors hover:text-primary'>
                                    Kontakt
                                </Link>
                            </li>
                        </ul>
                    </nav>

                    {socials.length > 0 ? (
                        <ul className='flex items-center gap-3'>
                            {socials.map(social => {
                                const Icon = socialIcons[social.network]

                                return (
                                    <li key={social.href}>
                                        <a
                                            href={social.href}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            aria-label={social.label}
                                            className='flex size-10 items-center justify-center rounded-full bg-ink text-white transition-colors hover:bg-primary'
                                        >
                                            <Icon className='size-4' aria-hidden='true' />
                                        </a>
                                    </li>
                                )
                            })}
                        </ul>
                    ) : (
                        <p className='flex items-center gap-2 text-sm text-muted-foreground'>
                            <MapPin className='size-4 text-primary' aria-hidden='true' />
                            {company.location}
                        </p>
                    )}
                </div>

                {(company.phone || company.email) && (
                    <div className='mt-8 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm'>
                        {company.phone && (
                            <a href={company.phoneHref} className='flex items-center gap-2 font-medium transition-colors hover:text-primary'>
                                <Phone className='size-4 text-primary' aria-hidden='true' />
                                {company.phone}
                            </a>
                        )}

                        {company.email && (
                            <a href={`mailto:${company.email}`} className='flex items-center gap-2 font-medium transition-colors hover:text-primary'>
                                <Mail className='size-4 text-primary' aria-hidden='true' />
                                {company.email}
                            </a>
                        )}
                    </div>
                )}

                <div className='mt-10 flex flex-col items-center justify-center gap-3 border-t border-border pt-6 text-xs text-muted-foreground sm:flex-row sm:gap-6'>
                    <p>
                        &copy; <span suppressHydrationWarning>{new Date().getFullYear()}</span> {siteConfig.name}. Sva prava zadržana.
                    </p>

                    <Link href='/privatnost' className='transition-colors hover:text-primary'>
                        Politika privatnosti
                    </Link>
                </div>
            </Container>
        </footer>
    )
}
